import { FieldDefinition } from '../../00_modules/core/product/FieldDefinition';
import { FieldType } from '../../00_modules/core/product/FieldType';
import { GroupedField, PageField } from '../../00_modules/core/product/GroupedField';

import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { patch, updateItem } from '@ngxs/store/operators';
import { SetPageVisibility, SetSectionVisibility, UpdateField, UpdateFields } from '../action/field-definition.action';

export interface FieldDefinitionStateModel {
    fieldDefinitions: FieldDefinition[];
}

const basicData = 'basicdata';
const basicDataDash = 'basicdata-';
const salvageExchange = 'salvageExchange';
const salvageExchangeDash = 'salvageExchange-';
const valueSummaryDash = 'valuesummary-';
const valueSummary = 'valuesummary';
const tireData = 'tireData';
const notifications = 'notifications';
const accounting = 'accounting';
const textBlocks = 'textBlocks';
const textBlocDash = 'textBlocks-';
const vehicleDash = 'vehicle-';
const vehicle = 'vehicle';
const inspecDash = 'inspection-';
const inspec = 'inspection';
const expertAssessmentDash = 'expertAssessment-';
const expertAssessment = 'expertAssessment';
const damageDash = 'damage-';
const damage = 'damage';
const participantDash = 'participant-';
const participant = 'participant';
const photosDash = 'photos-';
const photos = 'photos';
const document = 'document';
const calculationDash = 'calculation-';
const calculation = 'calculation';
const valuationDash = 'valuation-';
const valuation = 'valuation';

@State<FieldDefinitionStateModel>({
    name: 'fieldDefinition',
    defaults: {
        fieldDefinitions: [],
    },
})
@Injectable()
export class FieldDefinitionState {
    @Selector()
    static getFieldDefinitions(state: FieldDefinitionStateModel) {
        return state.fieldDefinitions;
    }

    @Selector()
    static getBasicPageDefinition(state: FieldDefinitionStateModel) {
        const filteredBasicSectionFieldDefinitions = state.fieldDefinitions
            .filter(f => f.field.path.startsWith(basicDataDash.toUpperCase()) && f.field.fieldTypeId === FieldType.Section)
            .sort((a, b) => sortNumber(a.field.sortOrder, b.field.sortOrder));

        const filteredBasicFieldDefinition = state.fieldDefinitions
            .filter(f => f.field.path.startsWith(basicDataDash.toUpperCase()) && f.field.fieldTypeId > FieldType.Section)
            .sort((a, b) => sortNumber(a.field.sortOrder, b.field.sortOrder));

        const groupedBasicSectionFields = filteredBasicSectionFieldDefinitions.map(f => {
            const fields = filteredBasicFieldDefinition.filter(df => df.field.path.startsWith(f.field.path));
            return new GroupedField(f, fields);
        });

        const basicDataFieldDefinition = state.fieldDefinitions.find(f => f.field.path === basicData.toUpperCase());

        return new PageField(basicDataFieldDefinition, groupedBasicSectionFields);
    }

    @Selector()
    static getSalvageExchangePageDefinition(state: FieldDefinitionStateModel) {
        const salvageExchangeFieldDefinition = state.fieldDefinitions.find(f => f.field.path === salvageExchange.toUpperCase());

        const filteredSalvageExchangeSectionFieldDefinitions = state.fieldDefinitions
            .filter(f => f.field.path.startsWith(salvageExchangeDash.toUpperCase()) && f.field.fieldTypeId === FieldType.Section)
            .sort((a, b) => sortNumber(a.field.sortOrder, b.field.sortOrder));

        const FilteredSalvageExchangeFieldDefinitions = state.fieldDefinitions
            .filter(f => f.field.path.startsWith(salvageExchangeDash.toUpperCase()) && f.field.fieldTypeId > FieldType.Section)
            .sort((a, b) => sortNumbers(a.field.lineNumber, b.field.lineNumber, a.field.sortOrder, b.field.sortOrder));

        filteredSalvageExchangeSectionFieldDefinitions.map(f => {
            const fields = FilteredSalvageExchangeFieldDefinitions.filter(df => df.field.path.startsWith(f.field.path));
            return new GroupedField(f, fields);
        });

        const groupedSalvageExchangeSectionFields = filteredSalvageExchangeSectionFieldDefinitions.map(f => {
            const fields = FilteredSalvageExchangeFieldDefinitions.filter(df => df.field.path.startsWith(f.field.path));
            return new GroupedField(f, fields);
        });

        return new PageField(salvageExchangeFieldDefinition, groupedSalvageExchangeSectionFields);
    }

    @Selector()
    static getValueSummaryPageDefinition(state: FieldDefinitionStateModel) {
        const filteredValueSummarySectionFieldDefinitions = state.fieldDefinitions
            .filter(f => f.field.path.startsWith(valueSummaryDash.toUpperCase()) && f.field.fieldTypeId === FieldType.Section)
            .sort((a, b) => sortNumber(a.field.sortOrder, b.field.sortOrder));

        const filteredValueSummaryFieldDefinitions = state.fieldDefinitions
            .filter(f => f.field.path.startsWith(valueSummaryDash.toUpperCase()) && f.field.fieldTypeId > FieldType.Section)
            .sort((a, b) => sortNumbers(a.field.lineNumber, b.field.lineNumber, a.field.sortOrder, b.field.sortOrder));

        const groupedValueSummarySectionFields = filteredValueSummarySectionFieldDefinitions.map(f => {
            const fields = filteredValueSummaryFieldDefinitions.filter(df => df.field.path.startsWith(f.field.path));
            return new GroupedField(f, fields);
        });

        const valueSummaryFieldDefinition = state.fieldDefinitions.find(f => f.field.path === valueSummary.toUpperCase());

        return new PageField(valueSummaryFieldDefinition, groupedValueSummarySectionFields);
    }

    @Selector()
    static getTireDataPageDefinition(state: FieldDefinitionStateModel) {
        const tireDataFieldDefinition = state.fieldDefinitions.find(f => f.field.path === tireData.toUpperCase());

        const emptyDependentFields = Object.assign([], GroupedField);
        return new PageField(tireDataFieldDefinition, emptyDependentFields);
    }

    @Selector()
    static getNotificationsPageDefinition(state: FieldDefinitionStateModel) {
        const notificationsFieldDefinition = state.fieldDefinitions.find(f => f.field.path === notifications.toUpperCase());

        const emptyDependentFields = Object.assign([], GroupedField);
        return new PageField(notificationsFieldDefinition, emptyDependentFields);
    }

    @Selector()
    static getAccountingPageDefinition(state: FieldDefinitionStateModel) {
        const getAccountingPageDefinition = state.fieldDefinitions.find(f => f.field.path === accounting.toUpperCase());

        const emptyDependentFields = Object.assign([], GroupedField);
        return new PageField(getAccountingPageDefinition, emptyDependentFields);
    }

    @Selector()
    static getTextBlockPageDefinition(state: FieldDefinitionStateModel) {
        const textBlockFieldDefinition = state.fieldDefinitions.find(f => f.field.path === textBlocks.toUpperCase());

        const filteredTextBlocksSectionFieldDefinitions = state.fieldDefinitions
            .filter(f => f.field.path.startsWith(textBlocks.toUpperCase()) && f.field.fieldTypeId === FieldType.Section)
            .sort((a, b) => sortNumber(a.field.sortOrder, b.field.sortOrder));

        const filteredTextBlockVehicleFieldDefinitions = state.fieldDefinitions
            .filter(f => f.field.path.startsWith(textBlocDash.toUpperCase()) && f.field.fieldTypeId > FieldType.Section)
            .sort((a, b) => sortNumber(a.field.sortOrder, b.field.sortOrder));

        const groupedTextBlockSectionFields = filteredTextBlocksSectionFieldDefinitions.map(f => {
            const fields = filteredTextBlockVehicleFieldDefinitions.filter(df => df.field.path.startsWith(f.field.path));
            return new GroupedField(f, fields);
        });

        return new PageField(textBlockFieldDefinition, groupedTextBlockSectionFields);
    }

    @Selector()
    static getVehiclePageDefinition(state: FieldDefinitionStateModel) {
        const vehicleFieldDefinition = state.fieldDefinitions.find(f => f.field.path === vehicle.toUpperCase());

        const filteredVehicleSectionFieldDefinitions = state.fieldDefinitions
            .filter(f => f.field.path.startsWith(vehicleDash.toUpperCase()) && f.field.fieldTypeId === FieldType.Section)
            .sort((a, b) => sortNumber(a.field.sortOrder, b.field.sortOrder));

        const filteredVehicleFieldDefinitions = state.fieldDefinitions
            .filter(f => f.field.path.startsWith(vehicleDash.toUpperCase()) && f.field.fieldTypeId > FieldType.Section)
            .sort((a, b) => sortNumbers(a.field.lineNumber, b.field.lineNumber, a.field.sortOrder, b.field.sortOrder));

        const groupedVehicleSectionFields = filteredVehicleSectionFieldDefinitions.map(f => {
            const fields = filteredVehicleFieldDefinitions.filter(df => df.field.path.startsWith(f.field.path));
            return new GroupedField(f, fields);
        });

        return new PageField(vehicleFieldDefinition, groupedVehicleSectionFields);
    }

    @Selector()
    static getInspectionPageDefinition(state: FieldDefinitionStateModel) {
        const filteredInspectionSectionFieldDefinitions = state.fieldDefinitions
            .filter(f => f.field.path.startsWith(inspecDash.toUpperCase()) && f.field.fieldTypeId === FieldType.Section)
            .sort((a, b) => sortNumber(a.field.sortOrder, b.field.sortOrder));

        const filteredInspectionFieldDefinitions = state.fieldDefinitions
            .filter(f => f.field.path.startsWith(inspecDash.toUpperCase()) && f.field.fieldTypeId > FieldType.Section)
            .sort((a, b) => sortNumbers(a.field.lineNumber, b.field.lineNumber, a.field.sortOrder, b.field.sortOrder));

        const groupedInspectionSectionFields = filteredInspectionSectionFieldDefinitions.map(f => {
            const fields = filteredInspectionFieldDefinitions.filter(df => df.field.path.startsWith(f.field.path));
            return new GroupedField(f, fields);
        });

        const inspectionFieldDefinition = state.fieldDefinitions.find(f => f.field.path === inspec.toUpperCase());

        return new PageField(inspectionFieldDefinition, groupedInspectionSectionFields);
    }

    @Selector()
    static getExpertPageDefinition(state: FieldDefinitionStateModel) {
        const expertFieldDefinition = state.fieldDefinitions.find(f => f.field.path === expertAssessment.toUpperCase());
        const filteredExpertSectionFieldDefinitions = state.fieldDefinitions
            .filter(
                f => f.field.path.startsWith(expertAssessmentDash.toUpperCase()) && (f.field.fieldTypeId === FieldType.Section || f.field.fieldTypeId === FieldType.ListSection)
            )
            .sort((a, b) => sortNumber(a.field.sortOrder, b.field.sortOrder));

        const filteredExpertFieldDefinitions = state.fieldDefinitions
            .filter(f => f.field.path.startsWith(expertAssessmentDash.toUpperCase()) && f.field.fieldTypeId > FieldType.ListSection)
            .sort((a, b) => sortNumbers(a.field.lineNumber, b.field.lineNumber, a.field.sortOrder, b.field.sortOrder));

        const getGroupedExpertSectionFields = filteredExpertSectionFieldDefinitions.map(f => {
            const fields = filteredExpertFieldDefinitions.filter(df => df.field.path.startsWith(f.field.path.toUpperCase()) && f.field.fieldTypeId !== FieldType.ListSection);
            return new GroupedField(f, fields);
        });

        return new PageField(expertFieldDefinition, getGroupedExpertSectionFields);
    }

    @Selector()
    static getDamagePageDefinition(state: FieldDefinitionStateModel) {
        const damageFieldDefinition = state.fieldDefinitions.find(f => f.field.path === damage.toUpperCase());

        const filteredDamageSectionFieldDefinitions = state.fieldDefinitions
            .filter(f => f.field.path.startsWith(damageDash.toUpperCase()) && f.field.fieldTypeId === FieldType.BlockListSection)
            .sort((a, b) => sortNumber(a.field.sortOrder, b.field.sortOrder));

        const filteredDamageFieldDefinitions = state.fieldDefinitions
            .filter(f => f.field.path.startsWith(damageDash.toUpperCase()) && f.field.fieldTypeId > FieldType.BlockListSection)
            .sort((a, b) => sortNumbers(a.field.lineNumber, b.field.lineNumber, a.field.sortOrder, b.field.sortOrder));

        const groupedDamageSectionFields = filteredDamageSectionFieldDefinitions.map(f => {
            const fields = filteredDamageFieldDefinitions.filter(df => df.field.path.startsWith(f.field.path));
            return new GroupedField(f, fields);
        });

        return new PageField(damageFieldDefinition, groupedDamageSectionFields);
    }

    @Selector()
    static getParticipantsPageDefinition(state: FieldDefinitionStateModel) {
        const participantsFieldDefinition = state.fieldDefinitions.find(f => f.field.path === participant.toUpperCase());

        const filteredParticipantsSectionFieldDefinitions = state.fieldDefinitions
            .filter(f => f.field.path === participant.toUpperCase())
            .sort((a, b) => sortNumber(a.field.sortOrder, b.field.sortOrder));

        const filteredParticipantsFieldDefinitions = state.fieldDefinitions
            .filter(f => f.field.path.startsWith(participantDash.toUpperCase()))
            .sort((a, b) => sortNumbers(a.field.lineNumber, b.field.lineNumber, a.field.sortOrder, b.field.sortOrder));

        const groupedParticipantsSectionFields = filteredParticipantsSectionFieldDefinitions.map(f => {
            const fields = filteredParticipantsFieldDefinitions.filter(df => df.field.path.startsWith(f.field.path));
            return new GroupedField(f, fields);
        });

        return new PageField(participantsFieldDefinition, groupedParticipantsSectionFields);
    }

    @Selector()
    static getPhotoPageDefinition(state: FieldDefinitionStateModel) {
        const photoFieldDefinition = state.fieldDefinitions.find(f => f.field.path === photos.toUpperCase());

        const filteredPhotoSectionFieldDefinitions = state.fieldDefinitions
            .filter(f => f.field.path.startsWith(photosDash.toUpperCase()) && f.field.fieldTypeId === FieldType.Section)
            .sort((a, b) => sortNumber(a.field.sortOrder, b.field.sortOrder));

        const filteredPhotoFieldDefinitions = state.fieldDefinitions
            .filter(f => f.field.fieldTypeId === FieldType.Photo)
            .sort((a, b) => sortNumbersPhotos(a.sortOrder, b.sortOrder, a.field.sortOrder, b.field.sortOrder));

        const groupedPhotoSectionFields = filteredPhotoSectionFieldDefinitions.map(f => {
            const fields = filteredPhotoFieldDefinitions.filter(df => df.field.path.startsWith(f.field.path));
            return new GroupedField(f, fields);
        });

        return new PageField(photoFieldDefinition, groupedPhotoSectionFields);
    }

    @Selector()
    static getDocumentPageDefinition(state: FieldDefinitionStateModel) {
        const filteredDocumentSectionFieldDefinitions = state.fieldDefinitions
            .filter(f => f.field.fieldTypeId === FieldType.DocumentScope)
            .sort((a, b) => sortNumber(a.field.sortOrder, b.field.sortOrder));

        const filteredDocumentFieldDefinitions = state.fieldDefinitions
            .filter(f => f.field.fieldTypeId === FieldType.Document)
            .sort((a, b) => sortNumbers(a.field.lineNumber, b.field.lineNumber, a.field.sortOrder, b.field.sortOrder));

        const groupedDocumentSectionFields = filteredDocumentSectionFieldDefinitions.map(f => {
            const fields = filteredDocumentFieldDefinitions.filter(df => df.field.path.startsWith(f.field.path));
            return new GroupedField(f, fields);
        });

        const documentFieldDefinition = state.fieldDefinitions.find(f => f.field.path === document.toUpperCase());

        return new PageField(documentFieldDefinition, groupedDocumentSectionFields);
    }

    @Selector()
    static getCalculationPageDefinition(state: FieldDefinitionStateModel) {
        const filteredCalculationSectionFieldDefinitions = state.fieldDefinitions
            .filter(
                f => f.field.path.startsWith(calculationDash.toUpperCase()) && (f.field.fieldTypeId === FieldType.BlockListSection || f.field.fieldTypeId === FieldType.Section)
            )
            .sort((a, b) => sortNumber(a.field.sortOrder, b.field.sortOrder));

        const filteredCalculationFieldDefinitions = state.fieldDefinitions
            .filter(f => f.field.path.startsWith(calculationDash.toUpperCase()) && f.field.fieldTypeId > FieldType.BlockListSection)
            .sort((a, b) => sortNumbers(a.field.lineNumber, b.field.lineNumber, a.field.sortOrder, b.field.sortOrder));

        const groupedCalculationSectionFields = filteredCalculationSectionFieldDefinitions.map(f => {
            const fields = filteredCalculationFieldDefinitions.filter(df => df.field.path.startsWith(f.field.path));
            return new GroupedField(f, fields);
        });

        const calculationFieldDefinition = state.fieldDefinitions.find(f => f.field.path === calculation.toUpperCase());

        return new PageField(calculationFieldDefinition, groupedCalculationSectionFields);
    }

    @Selector()
    static getValuationPageDefinition(state: FieldDefinitionStateModel) {
        const filteredValuationSectionFieldDefinitions = state.fieldDefinitions
            .filter(f => f.field.path.startsWith(valuationDash.toUpperCase()) && (f.field.fieldTypeId === FieldType.BlockListSection || f.field.fieldTypeId === FieldType.Section))
            .sort((a, b) => sortNumber(a.field.sortOrder, b.field.sortOrder));

        const filteredValuationFieldDefinitions = state.fieldDefinitions
            .filter(f => f.field.path.startsWith(valuationDash.toUpperCase()) && f.field.fieldTypeId > FieldType.BlockListSection)
            .sort((a, b) => sortNumbers(a.field.lineNumber, b.field.lineNumber, a.field.sortOrder, b.field.sortOrder));
        const groupedValuationSectionFields = filteredValuationSectionFieldDefinitions.map(f => {
            const fields = filteredValuationFieldDefinitions.filter(df => df.field.path.startsWith(f.field.path));
            return new GroupedField(f, fields);
        });

        const valuationFieldDefinition = state.fieldDefinitions.find(f => f.field.path === valuation.toUpperCase());

        return new PageField(valuationFieldDefinition, groupedValuationSectionFields);
    }

    @Action(UpdateField)
    updateField(ctx: StateContext<FieldDefinitionStateModel>, action: UpdateField) {
        ctx.setState(
            patch<FieldDefinitionStateModel>({
                fieldDefinitions: updateItem<FieldDefinition>(fd => fd.id === action.fieldDefinition.id, action.fieldDefinition),
            })
        );
    }

    @Action(UpdateFields)
    updateFields(ctx: StateContext<FieldDefinitionStateModel>, action: UpdateFields) {
        const state = ctx.getState();
        ctx.setState({
            ...state,
            fieldDefinitions: action.fieldDefinitions,
        });
    }

    @Action(SetSectionVisibility)
    setSectionVisibility(ctx: StateContext<FieldDefinitionStateModel>, action: SetSectionVisibility) {
        const groupedField = { ...action.groupedField } as GroupedField;
        if (groupedField.dependentFields.length > 0) {
            const sVisibility = Math.max(...groupedField.dependentFields.map(df => df.visibilityTypId));

            let updatedGroupField = Object.assign({}, groupedField.sectionField);
            updatedGroupField.visibilityTypId = sVisibility;
            groupedField.sectionField = updatedGroupField;
        }

        ctx.setState(
            patch<FieldDefinitionStateModel>({
                fieldDefinitions: updateItem<FieldDefinition>(gf => gf.id === action.groupedField.sectionField.id, groupedField.sectionField),
            })
        );
    }

    @Action(SetPageVisibility)
    setPageVisibility(ctx: StateContext<FieldDefinitionStateModel>, action: SetPageVisibility) {
        const pageField = { ...action.pageField } as PageField;
        const pVisibility = Math.max(...pageField.dependentFields.map(df => df.sectionField.visibilityTypId));

        let updatedPageField = Object.assign({}, pageField.pageField);
        updatedPageField.visibilityTypId = pVisibility;
        pageField.pageField = updatedPageField;

        ctx.setState(
            patch<FieldDefinitionStateModel>({
                fieldDefinitions: updateItem<FieldDefinition>(gf => gf.id === action.pageField.pageField.id, updatedPageField),
            })
        );
    }
}

function sortNumber(a: number, b: number): number {
    return a - b;
}

function sortNumbers(a: number, b: number, c: number, d: number): number {
    const diff = a - b;
    if (diff !== 0) {
        return diff;
    }
    return c - d;
}

function sortNumbersPhotos(a: number, b: number, c: number, d: number): number {
    if (a === null && b === 0) {
        return c - d;
    }

    if (a === null && b !== null) {
        return 100;
    }

    if (a !== null && b === null) {
        return -100;
    }

    const diff = a - b;
    if (diff !== 0) {
        return diff;
    }
    return c - d;
}
